<template>
  <div class="w-full">
    <component
      :is="answerComponent"
      :question="question"
      :inline="inline"
      @on-press="onAnswerPress"
    />
    <div
      v-if="question.optional && question.type.slug !== QuestionType.TEXT_INPUT"
      class="flex flex-row justify-end mt-2"
    >
      <base-button
        @click="onAnswerPress({value: [undefined]})"
      >
        <span class="font-content">Suivant</span>
      </base-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Wrapper component for a survey question.
 *
 * It dynamically selects and renders the appropriate answer component based on the question type.
 * It also handles the "Next" button for optional questions.
 */

import BaseButton from '@/core/components/base/BaseButton.vue';
import {BaseSurveyQuestion, QuestionType, SurveyQuestion} from "@/core/interfaces/survey/Survey";
import {type Component, computed, ref} from "vue";
import {
    type SurveyAnswerComponentProps,
    SurveyAnswerMultipleChoice,
    SurveyAnswerSingleChoice, SurveyAnswerTextInput,
    SurveyAnswerYesNo, SurveyAnswerYesNoWithProducts
} from "@/survey/components/survey-modal/answers";

const {inline = false, question} = defineProps<{
    question: SurveyQuestion;
    inline?: boolean;
}>();

const emits = defineEmits<{
    (e: 'onAnswerPress', answer: BaseSurveyQuestion['user_answer']): void;
}>();

const localQuestion = ref(question as SurveyQuestion);

const componentMap = {
    [QuestionType.YES_NO]: SurveyAnswerYesNo,
    [QuestionType.SINGLE_CHOICE]: SurveyAnswerSingleChoice,
    [QuestionType.MULTIPLE_CHOICE]: SurveyAnswerMultipleChoice,
    [QuestionType.TEXT_INPUT]: SurveyAnswerTextInput,
    [QuestionType.CHECK_INPUT]: SurveyAnswerTextInput,
    [QuestionType.DATE]: SurveyAnswerTextInput,
    [QuestionType.YES_NO_WITH_PRODUCTS]: SurveyAnswerYesNoWithProducts,
} as const satisfies Record<QuestionType, Component<SurveyAnswerComponentProps>>;

const answerComponent = computed<Component<SurveyAnswerComponentProps>>(() => {
    return componentMap[localQuestion.value.type.slug] || SurveyAnswerSingleChoice;
});

const onAnswerPress = (answer: BaseSurveyQuestion['user_answer']) => {
    emits('onAnswerPress', answer);
};
</script>
